import React, { useEffect, useRef } from 'react';
import ScrollContainer from 'react-indiana-drag-scroll';
import Paginator from '../../../shared/Paginator';
import TableLoader from '../../../shared/TableLoader';
import NoDataFound from '../../../shared/NoDataFound';

// Import modular components
import { 
    ActionsMenu, 
    ImageModal, 
    TimelineModal,
    ContentDrawer, 
    VersionsRow 
} from '../components';

const AdTable = ({ 
    columns, 
    data, 
    loading = false, 
    totalPages, 
    currentPage, 
    onPageClick,
    isPending,
    designers,
    contentWriters,
    showActionsMenu,
    modal,
    drawer,
    expandedVersionsRow,
    actionHandlers,
    renderCell,
    getModalAlt,
    closeExpandedRows,
    handleDesignerChange,
    handleContentWriterChange,
    handleGoToLive,
    goLiveLoadingIdx,
    fetchContent
}) => {
    const tableRef = useRef(null);

    // Handle clicking outside the table to close expanded rows
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                tableRef.current &&
                !tableRef.current.contains(event.target) &&
                (expandedVersionsRow !== null || showActionsMenu !== -1)
            ) {
                closeExpandedRows();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [expandedVersionsRow, showActionsMenu, closeExpandedRows]);

    const renderTableCell = (key, value, row, rowIndex) => {
        if (key === 'action') {
            return (
                <ActionsMenu
                    rowIndex={rowIndex}
                    row={row}
                    showActionsMenu={showActionsMenu}
                    actionHandlers={actionHandlers}
                    expandedVersionsRow={expandedVersionsRow}
                />
            );
        }
        return renderCell(key, value, row, designers, contentWriters, isPending, handleDesignerChange, handleContentWriterChange, handleGoToLive, goLiveLoadingIdx, rowIndex, fetchContent);
    };

    return (
        <div ref={tableRef}>
            <ScrollContainer>
                <table className="table table-bordered w-full">
                    <thead className="sticky top-0 bg-gray-50">
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    className="px-4 py-3  font-semibold text-gray-600 uppercase tracking-wider whitespace-nowrap"
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    {loading ? (
                        <TableLoader colspan={columns.length} />
                    ) : data.length > 0 ? (
                        <tbody>
                            {data.map((row, rowIndex) => (
                                <React.Fragment key={rowIndex}>
                                    <tr className={`border-t hover:bg-gray-50 transition-colors ${rowIndex %2 === 0 ? '' : 'bg-gray-100'}`}>
                                        {columns.map((col) => (
                                            <td key={col.key} className="relative w-full text-center">
                                                {renderTableCell(col.key, row[col.key], row, rowIndex)}
                                            </td>
                                        ))}
                                    </tr>
                                    <VersionsRow
                                        row={row}
                                        isExpanded={expandedVersionsRow === rowIndex}
                                        colSpan={columns.length}
                                        onClose={() => closeExpandedRows()}
                                    />
                                </React.Fragment>
                            ))}
                        </tbody>
                    ) : (
                        <NoDataFound colspan={columns.length} />
                    )}
                </table>
            </ScrollContainer>

            {!loading && totalPages > 1 && onPageClick && (
                <div className="mt-4 flex justify-end">
                    <Paginator 
                        pageCount={totalPages} 
                        handlePageClick={onPageClick} 
                        forcePage={currentPage || 0} 
                    />
                </div>
            )}
            
            <ImageModal
                isOpen={modal.open && modal.type === 'latestDesign'}
                image={modal.image}
                alt={getModalAlt(modal.type)}
                onClose={actionHandlers.closeModal}
            />

            <TimelineModal
                isOpen={modal.open && modal.type === 'timeline'}
                row={modal.timelineData}
                onClose={actionHandlers.closeModal}
            />

            <ContentDrawer
                isOpen={drawer.open}
                type={drawer.type}
                rowData={drawer.rowData}
                onClose={actionHandlers.closeDrawer}
            />
        </div>
    );
};

export default AdTable;
